// Lightweight pixel loader and event bridge for TikTok + Meta
// TikTok's base pixel (load + page view) is installed directly in index.html's <head>
// per TikTok's setup requirements, driven by the same VITE_TIKTOK_PIXEL_ID env var via
// Vite's %VAR% HTML replacement. Only the Meta pixel is bootstrapped here; trackPixels()
// below still drives event calls for both via window.ttq / window.fbq.
import { ENV } from '../config/env.js';

const META_ID = ENV.analytics.metaPixelId;
const DEBUG = ENV.analytics.debug;

let pixelsInitialized = false;

export function initPixels() {
  if (pixelsInitialized) return;
  try {
    if (META_ID && typeof window !== 'undefined') {
      if (DEBUG) console.info('[pixels] init Meta', META_ID);
      // Meta (Facebook) Pixel
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      try { window.fbq('init', META_ID); window.fbq('track', 'PageView'); if (DEBUG) console.info('[pixels] Meta PageView queued'); } catch (e) { if (DEBUG) console.warn('[pixels] Meta error', e); }
    }

    pixelsInitialized = true;
    if (DEBUG) console.info('[pixels] initialized');
  } catch (_) {
    // swallow pixel init errors
  }
}

// Map a few common events to both pixels
export function trackPixels(event, params = {}) {
  try {
    if (typeof window === 'undefined') return;
    const p = params || {};
    if (DEBUG) console.info('[pixels] track', event, p);

    // PageView
    if (event === 'PageView' || event === 'page_view') {
      try { if (window.ttq) { window.ttq.page(); if (DEBUG) console.info('[pixels] TikTok PageView'); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok PageView error', e); }
      try { if (window.fbq) { window.fbq('track', 'PageView'); if (DEBUG) console.info('[pixels] Meta PageView'); } } catch (e) { if (DEBUG) console.warn('[pixels] Meta PageView error', e); }
      return;
    }

    // Checkout start
    if (event === 'InitiateCheckout' || event === 'begin_checkout') {
      try { if (window.ttq) { window.ttq.track('InitiateCheckout', sanitizeTikTokParams(p)); if (DEBUG) console.info('[pixels] TikTok InitiateCheckout'); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok InitiateCheckout error', e); }
      try { if (window.fbq) { window.fbq('track', 'InitiateCheckout', sanitizeMetaParams(p)); if (DEBUG) console.info('[pixels] Meta InitiateCheckout'); } } catch (e) { if (DEBUG) console.warn('[pixels] Meta InitiateCheckout error', e); }
      return;
    }

    // Purchase
    if (event === 'Purchase' || event === 'purchase' || event === 'subscribe') {
      try { if (window.ttq) { window.ttq.track('Purchase', sanitizeTikTokParams(p)); if (DEBUG) console.info('[pixels] TikTok Purchase'); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok Purchase error', e); }
      try { if (window.fbq) { window.fbq('track', 'Purchase', sanitizeMetaParams(p)); if (DEBUG) console.info('[pixels] Meta Purchase'); } } catch (e) { if (DEBUG) console.warn('[pixels] Meta Purchase error', e); }
      return;
    }

    // Lead
    if (event === 'Lead' || event === 'lead' || event === 'login_prompt') {
      try { if (window.ttq) { window.ttq.track('SubmitForm', sanitizeTikTokParams(p)); if (DEBUG) console.info('[pixels] TikTok Lead'); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok Lead error', e); }
      try { if (window.fbq) { window.fbq('track', 'Lead', sanitizeMetaParams(p)); if (DEBUG) console.info('[pixels] Meta Lead'); } } catch (e) { if (DEBUG) console.warn('[pixels] Meta Lead error', e); }
      return;
    }

    // Sign-in/sign-up modal opened — intent signal only, NOT a completed action. Custom
    // event (no TikTok/Meta standard equivalent fits "viewed the auth form" specifically)
    // so it can't get conflated with Lead/CompleteRegistration in reporting.
    if (event === 'ViewRegistration') {
      try { if (window.ttq) { window.ttq.track('ViewRegistration', sanitizeTikTokParams(p)); if (DEBUG) console.info('[pixels] TikTok ViewRegistration'); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok ViewRegistration error', e); }
      try { if (window.fbq) { window.fbq('trackCustom', 'ViewRegistration', sanitizeMetaParams(p)); if (DEBUG) console.info('[pixels] Meta ViewRegistration'); } } catch (e) { if (DEBUG) console.warn('[pixels] Meta ViewRegistration error', e); }
      return;
    }

    // First successful generation ever, this browser — the real "aha moment" for
    // FlashFonic. Fired once (see the dedupe in firebase/config.js's trackEvent) so a
    // single enthusiastic user generating a dozen decks doesn't inflate the activation
    // count — ad platforms should see "how many acquired people reached value," not
    // "how many things got generated."
    if (event === 'FirstGeneration') {
      try { if (window.ttq) { window.ttq.track('FirstGeneration', sanitizeTikTokParams(p)); if (DEBUG) console.info('[pixels] TikTok FirstGeneration'); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok FirstGeneration error', e); }
      try { if (window.fbq) { window.fbq('trackCustom', 'FirstGeneration', sanitizeMetaParams(p)); if (DEBUG) console.info('[pixels] Meta FirstGeneration'); } } catch (e) { if (DEBUG) console.warn('[pixels] Meta FirstGeneration error', e); }
      return;
    }

    // Registration complete
    if (event === 'CompleteRegistration' || event === 'sign_up') {
      try { if (window.ttq) { window.ttq.track('CompleteRegistration', sanitizeTikTokParams(p)); if (DEBUG) console.info('[pixels] TikTok CompleteRegistration'); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok CompleteRegistration error', e); }
      try { if (window.fbq) { window.fbq('track', 'CompleteRegistration', sanitizeMetaParams(p)); if (DEBUG) console.info('[pixels] Meta CompleteRegistration'); } } catch (e) { if (DEBUG) console.warn('[pixels] Meta CompleteRegistration error', e); }
      return;
    }

    // Fallback: try to forward as-is to TikTok custom event
    try { if (window.ttq) { window.ttq.track(event, sanitizeTikTokParams(p)); if (DEBUG) console.info('[pixels] TikTok custom', event); } } catch (e) { if (DEBUG) console.warn('[pixels] TikTok custom error', e); }
  } catch (_) {
    // ignore
  }
}

function sanitizeMetaParams(p) {
  const out = {};
  if (p.value != null) out.value = p.value;
  if (p.currency) out.currency = p.currency;
  if (p.content_name) out.content_name = p.content_name;
  if (p.content_ids) out.content_ids = p.content_ids;
  if (p.contents) out.contents = p.contents;
  if (p.num_items != null) out.num_items = p.num_items;
  return out;
}

function sanitizeTikTokParams(p) {
  const out = {};
  if (p.value != null) out.value = p.value;
  if (p.currency) out.currency = p.currency;
  if (p.content_name) out.content_name = p.content_name;
  if (p.content_id) out.content_id = p.content_id;
  if (p.contents) out.contents = p.contents;
  if (p.quantity != null) out.quantity = p.quantity;
  return out;
}
